<template>
  <div :class="$style.dialogOverlay">
    <!-- 알림 컴포넌트 제거 -->
    <!-- <Notification ... /> -->

    <div :class="$style.dialogContainer">
      <!-- 제목 및 설명 (1단계에서만 표시) -->
      <div :class="$style.headerSection">
        <!-- 닫기 버튼 -->
        <button :class="$style.closeButton" @click="$emit('close')" aria-label="닫기">
          <img :src="closeIconUrl" alt="닫기" />
        </button>
        <h2 :class="$style.title">상담을 평가 부탁드려요</h2>

        <div style="width: 16px"></div>
      </div>

      <!-- 피드백 폼 (아직 제출되지 않았을 때) -->
      <div v-if="!isSubmitted" :class="$style.formContainer">
        <div :class="$style.subHeaderSection">
          <p v-if="currentStep === 1" :class="$style.description">
            주신 평가는 모든 고객분들의<br />
            상담 만족도 향상에 큰 힘이 됩니다. 😄
          </p>
          <!-- 답변된 요약 영역 (클릭 가능) -->
          <transition name="fade">
            <div :class="$style.answeredSummaries" v-if="currentStep > 1">
              <!-- 선택지 답변 요약 -->
              <div
                v-if="choiceRating > 0"
                :key="'answered-1'"
                :class="[$style.answeredSection, { [$style.clickable]: currentStep !== 1 && !isTransitioning }]"
                @click="goToStep(1)"
              >
                <label :class="$style.questionLabelSummary">선택지로 답변하는 상담</label>
                <div :class="$style.starsContainerSummary">
                  <img
                    v-for="n in choiceRating"
                    :key="`choice-answered-${n}`"
                    :src="starFilledIconUrl"
                    :class="$style.answeredStar"
                    alt=""
                  />
                  <img
                    v-for="n in 5 - choiceRating"
                    :key="`choice-empty-${n}`"
                    :src="starOutlineIconUrl"
                    :class="$style.answeredStar"
                    alt=""
                  />
                </div>
                <!-- <span :class="$style.ratingDescriptionSummary">{{ getRatingDescription(choiceRating) }}</span> -->
              </div>
              <!-- 직접 문의 답변 요약 -->
              <div
                v-if="shouldIncludeDirectQueryStep && directQueryRating > 0 && currentStep > 2"
                :key="'answered-2'"
                :class="[$style.answeredSection, { [$style.clickable]: currentStep !== 2 && !isTransitioning }]"
                @click="goToStep(2)"
              >
                <label :class="$style.questionLabelSummary">채팅으로 문의하는 상담</label>
                <div :class="$style.starsContainerSummary">
                  <img
                    v-for="n in directQueryRating"
                    :key="`direct-answered-${n}`"
                    :src="starFilledIconUrl"
                    :class="$style.answeredStar"
                    alt=""
                  />
                  <img
                    v-for="n in 5 - directQueryRating"
                    :key="`direct-empty-${n}`"
                    :src="starOutlineIconUrl"
                    :class="$style.answeredStar"
                    alt=""
                  />
                </div>
                <!-- <span :class="$style.ratingDescriptionSummary">{{ getRatingDescription(directQueryRating) }}</span> -->
              </div>
            </div>
          </transition>
        </div>

        <!-- 현재 단계 활성 질문 영역 (슬라이드 애니메이션 적용) -->
        <div :class="$style.activeQuestionArea">
          <transition name="step-slide" mode="out-in">
            <!-- 단계 1: 선택지 답변 -->
            <div v-if="currentStep === 1" key="step-1" :class="$style.stepContent">
              <label :class="$style.questionLabel">선택지로 답변하는 상담은 어떠셨나요?</label>
              <div :class="$style.starsContainer">
                <button
                  v-for="n in 5"
                  :key="`choice-${n}`"
                  :class="[$style.starButton, { [$style.selected]: choiceRating === n }]"
                  @click="selectChoiceRating(n)"
                  :aria-label="`${n}점`"
                  :disabled="isTransitioning"
                >
                  <img :src="choiceRating >= n ? starFilledIconUrl : starOutlineIconUrl" alt="" />
                </button>
              </div>
              <!-- 별점 선택 시 잠시 텍스트 표시 -->
              <span :class="[$style.ratingDescription, { [$style.visible]: choiceRating > 0 }]">{{
                getRatingDescription(choiceRating)
              }}</span>
            </div>

            <!-- 단계 2: 직접 문의 답변 -->
            <div v-else-if="shouldIncludeDirectQueryStep && currentStep === 2" key="step-2" :class="$style.stepContent">
              <label :class="$style.questionLabel">채팅으로 문의하는 상담은 어떠셨나요?</label>
              <div :class="$style.starsContainer">
                <button
                  v-for="n in 5"
                  :key="`direct-${n}`"
                  :class="[$style.starButton, { [$style.selected]: directQueryRating === n }]"
                  @click="selectDirectQueryRating(n)"
                  :aria-label="`${n}점`"
                  :disabled="isTransitioning"
                >
                  <img :src="directQueryRating >= n ? starFilledIconUrl : starOutlineIconUrl" alt="" />
                </button>
              </div>
              <span :class="[$style.ratingDescription, { [$style.visible]: directQueryRating > 0 }]">{{
                getRatingDescription(directQueryRating)
              }}</span>
            </div>

            <!-- 단계 3: 아쉬운 점 주관식 -->
            <div v-else-if="currentStep === finalStep" key="step-final" :class="$style.stepContent">
              <label :class="$style.questionLabel">말씀해주실 개선사항이 있을까요? (선택사항)</label>
              <textarea
                v-model="improvementText"
                :class="$style.feedbackTextarea"
                placeholder="상담 과정에서 불편하셨거나 아쉬웠던 부분이 있으면 무엇이든 말씀 부탁드려요!"
                rows="3"
              ></textarea>
            </div>
          </transition>
        </div>

        <!-- 제출 버튼 -->
        <button :class="$style.submitButton" @click="handleSubmit" :disabled="submitDisabled || loading">
          {{ loading ? "제출 중..." : "평가완료" }}
        </button>
      </div>

      <!-- 피드백 제출 완료 상태 -->
      <div v-else :class="$style.submittedContainer">
        <h2 :class="$style.title">소중한 의견 감사합니다</h2>
        <p :class="$style.description">피드백 작성이 완료되었습니다.</p>
        <!-- 필요시 추가 내용 -->
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, useCssModule } from "vue"
import type { CreateCustomerFeedback, CustomerFeedback, Ticket } from "@wowpress/types"
import closeIconUrl from "@/assets/icons/close-icon.svg"
import starFilledIconUrl from "@/assets/icons/star-filled.svg"
import starOutlineIconUrl from "@/assets/icons/star-outline.svg"
import { useFeedback } from "@/composables/useFeedback" // useFeedback 임포트
// import Notification from '@/components/Notification.vue'; // Notification 컴포넌트 임포트 제거

const props = defineProps<{
  ticket: Ticket
}>()

const emit = defineEmits(["close", "show-notification"]) // 'show-notification' 이벤트 추가

const $style = useCssModule()
const { saveFeedback, loading, error } = useFeedback() // useFeedback 사용

// 로컬 알림 상태 제거
// const notification = ref<{ type: 'info' | 'error'; message: string } | null>(null);

// 로컬 알림 함수 제거
// const showNotification = (payload: { type: 'info' | 'error'; message: string }) => { ... };
// const closeNotification = () => { ... };

const currentStep = ref<number>(1)
const choiceRating = ref<number>(0)
const directQueryRating = ref<number>(0)
const improvementText = ref<string>("")
const isTransitioning = ref(false)
const transitionDuration = 400 // 슬라이드 애니메이션 시간 (ms) - 증가 및 CSS와 동기화
const ratingDisplayDelay = 800 // 별점 설명 표시 후 다음 단계 전환 지연 시간 (ms)

// 피드백이 이미 제출되었는지 확인
const isSubmitted = computed(() => !!props.ticket.customerFeedbackId)

const shouldIncludeDirectQueryStep = computed(() => !!props.ticket.assignedTo)
const finalStep = computed(() => (shouldIncludeDirectQueryStep.value ? 3 : 2))

const submitDisabled = computed(() => {
  // 제출된 상태이거나, 마지막 단계 아니거나, 전환 중이면 비활성화
  if (isSubmitted.value || currentStep.value !== finalStep.value || isTransitioning.value) return true
  if (choiceRating.value === 0) return true
  if (shouldIncludeDirectQueryStep.value && directQueryRating.value === 0) return true
  return false
})

// 특정 단계로 이동 (답변 요약 클릭 시)
const goToStep = (step: number) => {
  // 현재 단계이거나 전환 중이면 무시
  if (currentStep.value === step || isTransitioning.value) return

  isTransitioning.value = true
  currentStep.value = step // 즉시 상태 변경하여 애니메이션 시작

  // 애니메이션 완료 후 전환 상태 해제
  setTimeout(() => {
    isTransitioning.value = false
  }, transitionDuration) // CSS 애니메이션 시간과 일치
}

const selectChoiceRating = (rating: number) => {
  if (isTransitioning.value) return

  // 별점 즉시 업데이트 (텍스트 설명 표시)
  choiceRating.value = rating

  // 0점이 아닐 때만 진행 (같은 별점 클릭 시에도 진행)
  if (rating > 0) {
    isTransitioning.value = true // 추가 클릭 방지

    // 지연 시간 후 다음 단계로 전환 시작
    setTimeout(() => {
      // currentStep 변경하여 애니메이션 트리거
      currentStep.value = shouldIncludeDirectQueryStep.value ? 2 : finalStep.value

      // 애니메이션 끝날 때 isTransitioning 해제
      setTimeout(() => {
        isTransitioning.value = false
      }, transitionDuration) // CSS 애니메이션 시간과 일치
    }, ratingDisplayDelay)
  }
}

const selectDirectQueryRating = (rating: number) => {
  if (isTransitioning.value) return

  directQueryRating.value = rating

  // 0점이 아닐 때만 진행 (같은 별점 클릭 시에도 진행)
  if (rating > 0) {
    isTransitioning.value = true
    setTimeout(() => {
      // currentStep 변경하여 애니메이션 트리거
      currentStep.value = finalStep.value

      // 애니메이션 끝날 때 isTransitioning 해제
      setTimeout(() => {
        isTransitioning.value = false
      }, transitionDuration) // CSS 애니메이션 시간과 일치
    }, ratingDisplayDelay)
  }
}

// 피드백 제출 처리 (emit 사용하도록 수정)
const handleSubmit = async () => {
  if (loading.value) return

  // 알림 초기화 제거
  // closeNotification();

  const feedbackData: CreateCustomerFeedback = {
    choiceRating: choiceRating.value,
    directRating: shouldIncludeDirectQueryStep.value ? directQueryRating.value : null,
    companyName: props.ticket.customer.companyName ?? null,
    improvementText: improvementText.value.trim() || null,
    ticketId: props.ticket.id,
    agentId: props.ticket.assignedTo ?? null,
    agentName: null,
    userId: props.ticket.customer.id!,
  }

  console.log("[FeedbackDialog] Submitting feedback:", feedbackData)
  const success = await saveFeedback(feedbackData)

  if (success) {
    console.log("[FeedbackDialog] Feedback submitted successfully.")
    // 성공 알림 이벤트 발생
    emit("show-notification", { type: "info", message: "소중한 의견 감사합니다!" })
    // 다이얼로그 즉시 닫기 (setTimeout 제거)
    emit("close")
  } else {
    console.error("[FeedbackDialog] Feedback submission failed:", error.value)
    // 실패 알림 이벤트 발생
    const errorMessage = error.value?.message || "알 수 없는 오류가 발생했습니다."
    emit("show-notification", { type: "error", message: `피드백 제출 실패: ${errorMessage}` })
  }
}

const getRatingDescription = (rating: number): string => {
  // 0점일 경우 빈 문자열 반환 (초기 상태)
  if (rating === 0) return ""
  switch (rating) {
    case 1:
      return "개선이 필요해요"
    case 2:
      return "아쉬웠어요"
    case 3:
      return "보통이에요"
    case 4:
      return "만족했어요"
    case 5:
      return "매우 좋았어요"
    default:
      return "" // 혹시 모를 예외 처리
  }
}
</script>

<style module>
.dialogOverlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.dialogContainer {
  background-color: rgba(255, 255, 255, 0.99);
  border-radius: 16px;
  padding: 24px;
  width: 307px;
  border: 1px transparent;
  box-shadow: 0px -4px 16px 0px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* 내부 요소 간 간격 조정 */
  position: relative;
  font-family: "Noto Sans KR", sans-serif;
  max-height: 90vh;
  min-height: 320px;
  /* 제출 완료 화면 고려한 최소 높이 */
  overflow-y: auto;
  /* 세로 스크롤 가능 */
  overflow-x: hidden;
  /* 가로 스크롤 방지 */
}

.closeButton {
  background: none;
  border: none;
  cursor: pointer;
  margin-bottom: 3px;
}

.closeButton img {
  width: 14px;
  height: 14px;
  display: block;
}

.headerSection {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
}

.subHeaderSection {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  justify-content: center;
  min-height: 60px;
  width: 100%;
  position: relative;
  box-sizing: border-box;
}

/* 1단계 이후 설명 대신 높이 유지용 */
.headerPlaceholder {
  height: 36px;
  /* .description 높이와 유사하게 설정 */
}

.title {
  font-weight: 700;
  font-size: 20px;
  line-height: 1.2;
  letter-spacing: -0.025em;
  text-align: center;
  color: #000000;
  margin: 0;
  padding: 2px;
}

.description {
  font-weight: 400;
  font-size: 12px;
  line-height: 1.8;
  /* 2줄 기준 약 44px */
  letter-spacing: -0.0417em;
  text-align: center;
  color: #666;
  margin: 0;
  height: 36px;
  /* 2줄 고정 높이 */
  display: flex;
  /* 중앙 정렬 위해 */
  align-items: center;
  justify-content: center;
}

/* 답변된 요약들을 담는 컨테이너 */
.answeredSummaries {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  /* 활성 질문 영역과의 간격 */
}

/* 답변된 섹션 스타일 (요약용) */
.answeredSection {
  background-color: transparent;
  padding: 6px 8px;
  box-sizing: border-box;
  gap: 6px;
  display: flex;
  align-items: center;
  /* 세로 중앙 정렬 */
  transition: background-color 0.2s ease;
  border-radius: 4px;
  width: 100%;
  justify-content: space-between;
}

.answeredSection.clickable:hover {
  background-color: #f9f9f9;
  cursor: pointer;
}

.questionLabelSummary {
  font-size: 13px;
  font-weight: 400;
  color: #555;
  flex-shrink: 0;
  /* 별점 그룹을 오른쪽으로 밀기 */
  pointer-events: none;
}

.starsContainerSummary {
  display: flex;
  gap: 2px;
  /* 별 사이 간격 축소 */
  pointer-events: none;
}

.answeredStar {
  width: 14px;
  /* 별 크기 약간 축소 */
  height: 14px;
}

.ratingDescriptionSummary {
  font-size: 11px;
  color: #777;
  margin-left: 8px;
  /* 별점과의 간격 */
  pointer-events: none;
  white-space: nowrap;
  /* 줄바꿈 방지 */
}

/* 활성 질문 영역 컨테이너 */
.activeQuestionArea {
  position: relative;
  box-sizing: border-box;
  display: block;
  flex: 1;
  min-height: 0;
}
/* 각 스텝 내용을 감싸는 div */
.stepContent {
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 12px;
  box-sizing: border-box;
  width: 100%;
}

/* 활성 질문 영역 내부 스타일 */
.questionLabel {
  font-weight: 500;
  font-size: 15px;
  line-height: 1.4;
  text-align: left;
  color: #000000;
  text-align: center;
  white-space: break-spaces;
  margin-bottom: 12px;
}

.starsContainer {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.starButton {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  line-height: 0;
}

.starButton img {
  width: 24px;
  height: 24px;
  display: block;
  transition: transform 0.1s ease-in-out;
}

.starButton:hover img {
  transform: scale(1.1);
}

.ratingDescription {
  font-weight: 400;
  font-size: 12px;
  line-height: 1.6;
  letter-spacing: -0.0417em;
  text-align: center;
  color: #696969;
  margin-top: 8px;
  min-height: 1.6em;
  /* 설명 텍스트가 표시될 공간 확보 */
  opacity: 0;
  /* 기본 숨김 */
  transition: opacity 0.3s ease;
  /* 부드럽게 나타나도록 */
}

.ratingDescription.visible {
  opacity: 1;
  /* 보이도록 */
}

.feedbackTextarea {
  background-color: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 8px;
  padding: 12px;
  font-family: "Noto Sans KR", sans-serif;
  font-size: 12px;
  line-height: 1.6;
  color: #333;
  resize: vertical;
  min-height: 68px;
  width: 100%;
}

.feedbackTextarea::placeholder {
  color: #9a9a9a;
}

.submitButton {
  background-color: #d01578;
  border: 0.5px solid #d01578;
  border-radius: 8px;
  padding: 10px;
  font-weight: 700;
  font-size: 14px;
  line-height: 1.2;
  color: #ffffff;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    opacity 0.3s ease;
  height: 44px;
  box-shadow:
    0px 1px 4px 0px rgba(12, 12, 13, 0.05),
    0px 1px 4px 0px rgba(12, 12, 13, 0.1);
  margin-top: 8px;
  /* 활성 영역과의 간격 확보 */
}

.submitButton:hover:not(:disabled) {
  background-color: #a81061;
  border-color: #a81061;
}

.submitButton:disabled {
  background-color: #e5e7eb;
  border-color: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
  box-shadow: none;
}

/* --- Vue Transition Animations --- */

/* 활성 질문 슬라이드 전환 애니메이션 */
.step-slide-enter-active,
.step-slide-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  /* 부드러운 이징 함수로 변경 */
}

.step-slide-enter-from {
  opacity: 0;
  transform: translateX(30px);
  /* 이동 거리 약간 줄임 */
}

.step-slide-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

/* 요약 영역 페이드 애니메이션 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
  /* 페이드 효과 시간 */
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 버튼 페이드 애니메이션 (유지) */
.button-fade-enter-active,
.button-fade-leave-active {
  transition: opacity 0.5s ease;
}

.button-fade-enter-from,
.button-fade-leave-to {
  opacity: 0;
}

/* 폼과 완료 상태를 감싸는 컨테이너 (필요시 추가) */
.formContainer {
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* 내부 요소 간격 */
  flex: 1;
  /* 헤더/푸터 분리 */
  min-height: 0;
}

.submittedContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex: 1;
  /* 전체 높이 차지 */
  min-height: 120px;
  /* 최소 높이 */
  padding: 20px 0;
}

/* Notification 스타일 제거 */
/* .localNotification { ... } */
</style>
